import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OrganizationFeedbackDetails = () => {
  const { organizationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [organization, setOrganization] = useState(null);
  const [feedbackSettings, setFeedbackSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Get data from previous page
  const { feedbackId, rating, customerName } = location.state || {};
  
  const [formData, setFormData] = useState({
    additionalComments: '',
    categoryResponses: {}
  });

  useEffect(() => {
    if (!feedbackId || !rating || rating >= 4) {
      // Redirect if accessed incorrectly
      navigate(`/organization-feedback/${organizationId}`);
      return;
    }
    
    fetchOrganizationData();
  }, [organizationId, feedbackId, rating]);

  const fetchOrganizationData = async () => {
    try {
      const [orgResponse, settingsResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/organizations/${organizationId}/public`),
        axios.get(`${BACKEND_URL}/api/organizations/${organizationId}/feedback-settings`)
      ]);
      
      setOrganization(orgResponse.data);
      setFeedbackSettings(settingsResponse.data);
      
      // Initialize category responses
      const initialResponses = {};
      settingsResponse.data.categories?.forEach(category => {
        initialResponses[category.id] = '';
      });
      setFormData(prev => ({ ...prev, categoryResponses: initialResponses }));
      
    } catch (error) {
      setError('Failed to load feedback form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (categoryId, value) => {
    setFormData(prev => ({
      ...prev,
      categoryResponses: {
        ...prev.categoryResponses,
        [categoryId]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      // Submit detailed feedback
      await axios.put(`${BACKEND_URL}/api/organization-feedback/${feedbackId}`, {
        category_responses: formData.categoryResponses,
        additional_comments: formData.additionalComments
      });

      // Redirect to success page
      navigate(`/organization-feedback-success/${organizationId}`, {
        state: { rating, detailed: true }
      });
      
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to submit detailed feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading feedback form...</p>
        </div>
      </div>
    );
  }

  if (error && !organization) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Form</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(`/organization-feedback/${organizationId}`)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Default categories if not configured
  const defaultCategories = [
    {
      id: 'service_quality',
      name: 'Service Quality',
      options: ['Excellent', 'Good', 'Fair', 'Poor']
    },
    {
      id: 'staff_behavior',
      name: 'Staff Behavior',
      options: ['Excellent', 'Good', 'Fair', 'Poor']
    },
    {
      id: 'facility_cleanliness',
      name: 'Facility Cleanliness',
      options: ['Excellent', 'Good', 'Fair', 'Poor']
    },
    {
      id: 'overall_experience',
      name: 'Overall Experience',
      options: ['Excellent', 'Good', 'Fair', 'Poor']
    }
  ];

  const categories = feedbackSettings?.categories || defaultCategories;
  const commentsLabel = feedbackSettings?.comments_label || 'Tell us more about your experience';

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-orange-500 px-6 py-8 text-center">
            {organization?.logo && (
              <img
                src={organization.logo}
                alt={organization.name}
                className="w-16 h-16 mx-auto mb-4 rounded-full bg-white p-2"
              />
            )}
            <h1 className="text-xl font-bold text-white mb-2">
              Help Us Improve, {customerName}!
            </h1>
            <p className="text-orange-100 text-sm">
              Thank you for your feedback. Please help us understand how we can serve you better.
            </p>
            <div className="flex justify-center mt-3">
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  className={`text-2xl ${
                    star <= rating ? 'text-yellow-300' : 'text-orange-300'
                  }`}
                >
                  ⭐
                </span>
              ))}
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-8 space-y-8">
            {/* Category Questions */}
            {categories.map((category) => (
              <div key={category.id}>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {category.name} *
                </label>
                <div className="space-y-2">
                  {category.options.map((option) => (
                    <label key={option} className="flex items-center">
                      <input
                        type="radio"
                        name={category.id}
                        value={option}
                        checked={formData.categoryResponses[category.id] === option}
                        onChange={(e) => handleCategoryChange(category.id, e.target.value)}
                        className="mr-3 text-blue-600 focus:ring-blue-500"
                        required
                      />
                      <span className="text-sm text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}

            {/* Additional Comments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {commentsLabel}
              </label>
              <textarea
                value={formData.additionalComments}
                onChange={(e) => setFormData({ ...formData, additionalComments: e.target.value })}
                rows="4"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Please share any additional details that could help us improve our service..."
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Submit Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => navigate(`/organization-feedback/${organizationId}`)}
                className="flex-1 bg-gray-300 text-gray-700 py-3 px-4 rounded-md font-medium hover:bg-gray-400 transition-colors"
              >
                Go Back
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-md font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Submitting...' : 'Submit Detailed Feedback'}
              </button>
            </div>
          </form>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 text-center">
            <p className="text-xs text-gray-500">
              Your feedback is important to us and helps us provide better service.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationFeedbackDetails;